class CorrectedChlorideCalculator extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === "true") return;
    this.dataset.initialized = "true";
    var ROOT = this;
    "use strict";

/* ============================ ENGINE (pure) ============================ */
var REF={
  dog:{label:"Dog",nn:146,lo:107,hi:113},
  cat:{label:"Cat",nn:156,lo:117,hi:123},
  other:{label:"Other species",nn:null,lo:null,hi:null}
};
var CONV={cl:3.55,na:2.3};
var LIMITS={
  na:{min:80,max:220,sevLo:125,sevHi:170},
  cl:{min:40,max:180,extLo:80,extHi:140},
  nn:{min:130,max:170},
  ri:{min:80,max:160}
};
var UNITS={meq:"mEq/L",mmol:"mmol/L",mgdl:"mg/dL"};

function parseNum(raw){
  if(raw==null) return null;
  var s=String(raw).trim();
  if(s==="") return null;
  s=s.replace(/\s+/g,"");
  if(/^\d{1,3}(,\d{3})+(\.\d+)?$/.test(s)) return NaN; /* thousands separators: ambiguous, reject */
  if(/^\d+,\d+$/.test(s)) s=s.replace(",","."); /* decimal comma */
  if(!/^\d+(\.\d+)?$/.test(s)) return NaN;
  var v=parseFloat(s);
  return isFinite(v)?v:NaN;
}
function fmt(n){ /* ~4 significant figures, never exponent */
  if(n==null||!isFinite(n)) return "";
  if(n===0) return "0";
  if(Math.abs(n)>=1000) return String(Math.round(n));
  return String(Number(n.toPrecision(4)));
}
function fmt1(n){ /* 1 decimal place */
  if(n==null||!isFinite(n)) return "";
  return String(Math.round(n*10)/10);
}
function statusOf(v,lo,hi){ return v<lo?"low":(v>hi?"high":"ok"); }
function toMmol(v,unit,ion){ /* mEq/L = mmol/L; mg/dL divided by factor */
  if(v==null||!isFinite(v)) return v;
  if(unit==="mgdl") return v/CONV[ion];
  return v;
}

/* one field: returns {v: displayed mmol value (1 dp, so every surface rounds identically) or null, mmol, entered, unit, err} */
function readField(raw,unit,ion,lim){
  var p=parseNum(raw);
  if(p===null) return {v:null,err:"missing"};
  if(isNaN(p)) return {v:null,err:"Enter a plain number, decimals allowed, e.g. 132 or 132.5. No thousands separators."};
  var mmol=toMmol(p,unit,ion);
  var disp=Number(fmt1(mmol));
  if(disp<lim.min||disp>lim.max){
    var shown=(unit==="mgdl")?(fmt1(p)+" mg/dL = "+fmt1(disp)+" mmol/L"):(fmt1(disp)+" mEq/L");
    return {v:null,mmol:disp,entered:p,unit:unit,err:"Value "+shown+" is outside the accepted range ("+lim.min+" to "+lim.max+" mEq/L). Check the value and the unit."};
  }
  return {v:disp,mmol:disp,entered:p,unit:unit,err:null};
}

function computeCore(inp){
  var S={sp:inp.sp,errors:{},warnings:[],blocked:false,blockedMsg:"",flags:inp.flags||{}};

  var na=readField(inp.naRaw,inp.naUnit,"na",LIMITS.na);
  /* on bromide the measured "chloride" is mostly bromide and genuinely reads far above any true chloride, so widen the acceptance cap instead of implying a data-entry error */
  var clLim=S.flags.kbr?{min:LIMITS.cl.min,max:250}:LIMITS.cl;
  var cl=readField(inp.clRaw,inp.clUnit,"cl",clLim);
  if(na.err&&na.err!=="missing") S.errors.na=na.err;
  if(cl.err&&cl.err!=="missing"){
    if(cl.mmol!=null&&cl.mmol>180&&!S.flags.kbr) cl.err+=" A genuine analyser reading this high in a patient on potassium bromide is classic bromide interference; if this patient is on KBr, tick the bromide factor in step 4.";
    S.errors.cl=cl.err;
  }

  var nnP=parseNum(inp.nnRaw);
  var nn=null;
  if(nnP===null){}
  else if(isNaN(nnP)) S.errors.nn="Enter a plain number, e.g. 146.";
  else if(nnP<LIMITS.nn.min||nnP>LIMITS.nn.max) S.errors.nn="Normal sodium should be a reference-interval midpoint, typically 140 to 160 mEq/L (accepted "+LIMITS.nn.min+" to "+LIMITS.nn.max+").";
  else nn=Number(fmt(nnP));

  var loP=parseNum(inp.riLoRaw), hiP=parseNum(inp.riHiRaw);
  var riLo=null,riHi=null;
  if(loP===null||hiP===null){}
  else if(isNaN(loP)||isNaN(hiP)) S.errors.ri="Enter plain numbers for both ends of the interval.";
  else if(loP<LIMITS.ri.min||loP>LIMITS.ri.max||hiP<LIMITS.ri.min||hiP>LIMITS.ri.max) S.errors.ri="Interval values should be between "+LIMITS.ri.min+" and "+LIMITS.ri.max+" mEq/L.";
  else if(loP>=hiP) S.errors.ri="The low end must be below the high end.";
  else {riLo=loP;riHi=hiP;}

  var missing=[];
  if(na.v==null) missing.push("measured sodium");
  if(cl.v==null) missing.push("measured chloride");
  if(nn==null) missing.push("normal sodium (step 3)");
  if(riLo==null) missing.push("chloride reference interval (step 3)");
  if(missing.length){
    S.blocked=true;
    var anyErr=S.errors.na||S.errors.cl||S.errors.nn||S.errors.ri;
    S.blockedKind=anyErr?"errors":((na.v==null||cl.v==null)?"nacl":"ref");
    S.blockedMsg=anyErr?"Fix the highlighted values to calculate.":"Enter "+missing.join(", ")+" to calculate.";
    return S;
  }

  S.na=na; S.cl=cl; S.nn=nn; S.riLo=riLo; S.riHi=riHi;

  /* deterministic working from displayed operands */
  var ratio=Number(fmt(nn/na.v));
  var corr=Number(fmt1(ratio*cl.v));
  var water=Number(fmt1(corr-cl.v));
  S.ratio=ratio; S.corr=corr; S.water=water;
  S.stM=statusOf(cl.v,riLo,riHi);
  S.stC=statusOf(corr,riLo,riHi);

  S.convLines=[];
  if(na.unit==="mgdl") S.convLines.push("Measured Na+ = "+fmt(na.entered)+" mg/dL / "+CONV.na+" = "+fmt(na.v)+" mmol/L");
  if(cl.unit==="mgdl") S.convLines.push("Measured Cl- = "+fmt(cl.entered)+" mg/dL / "+CONV.cl+" = "+fmt(cl.v)+" mmol/L");
  S.workingLines=S.convLines.concat([
    "Ratio = normal Na+ / measured Na+ = "+fmt(nn)+" / "+fmt(na.v)+" = "+fmt(ratio),
    "Corrected Cl- = "+fmt(ratio)+" x "+fmt(cl.v)+" = "+fmt1(corr)+" mEq/L"
  ]);

  /* interpretation */
  var stWord={ok:"within the reference interval",low:"below the reference interval",high:"above the reference interval"};
  var I={};
  if(S.flags.kbr){
    I.title="Not interpretable: patient on bromide";
    I.text="This patient is on bromide (KBr). Chloride analysers measure bromide as chloride, so the measured value, and therefore the corrected value, is spuriously raised and cannot be interpreted. Obtain a serum bromide level or use a chloride method validated as bromide-insensitive. Do not treat this chloride number.";
  } else if(S.stC==="low"){
    I.title="Corrected hypochloraemia";
    I.text="Chloride has been lost in excess of sodium: a true chloride disturbance"+(S.stM==="ok"?" that the measured value alone was hiding":"")+". Think gastric loss (vomiting stomach contents, pyloric outflow obstruction), loop or thiazide diuretics, compensation for chronic respiratory acidosis, or bicarbonate administration. Expect a metabolic alkalosis, usually chloride-responsive; confirm with a blood gas. Chloride-replete fluids (0.9% NaCl, usually with KCl) plus treating the cause are the standard approach; mineralocorticoid-excess causes are chloride-resistant and need the endocrine cause treated.";
  } else if(S.stC==="high"){
    I.title="Corrected hyperchloraemia";
    I.text="Chloride has been gained in excess of sodium, or bicarbonate lost: a true chloride disturbance"+(S.stM==="ok"?" that the measured value alone was hiding":"")+". Think chloride-rich fluids (0.9% NaCl, hypertonic saline, KCl supplementation), small intestinal diarrhoea, renal tubular acidosis, carbonic anhydrase inhibitors, ammonium chloride, or compensation for chronic respiratory alkalosis. Expect a hyperchloraemic, normal anion gap metabolic acidosis; confirm with a blood gas and always exclude bromide therapy first.";
  } else if(S.stM==="low"){
    I.title="Relative hypochloraemia: free water effect";
    I.text="The corrected chloride is normal, so the low measured chloride is proportional to sodium's deviation below the entered normal value: a water effect, not a chloride problem. If the sodium is itself outside your laboratory's reference interval, work up and manage the water balance in its own right, and correct chronic sodium changes slowly.";
  } else if(S.stM==="high"){
    I.title="Relative hyperchloraemia: free water effect";
    I.text="The corrected chloride is normal, so the high measured chloride is proportional to sodium's deviation above the entered normal value: a water effect, not a chloride problem. If the sodium is itself outside your laboratory's reference interval, work up and manage the water balance in its own right, and correct chronic sodium changes slowly.";
  } else {
    I.title="No chloride disturbance";
    I.text="Measured and corrected chloride are both "+stWord.ok+". No chloride-specific action is indicated on these values.";
  }
  S.interp=I;

  /* warnings: lvl red 0 / amber 1 / info 2, kind sorts within level */
  var W=S.warnings;
  function w(lvl,kind,text){W.push({lvl:lvl,kind:kind,text:text});}
  if(S.flags.kbr) w("red",0,"Critical. On bromide (KBr): bromide is measured as chloride, so this result is spuriously raised and must not be interpreted or treated as chloride. Confirm with a serum bromide level or a bromide-insensitive method.");
  if(na.v<LIMITS.na.sevLo||na.v>LIMITS.na.sevHi) w("amber",1,"Caution. Severe dysnatraemia (Na+ "+fmt(na.v)+" mEq/L). Manage the free water disturbance in its own right, and correct chronic sodium changes slowly (commonly no faster than about 0.5 mEq/L per hour).");
  if((cl.v<LIMITS.cl.extLo||cl.v>LIMITS.cl.extHi)&&!S.flags.kbr) w("amber",2,"Caution. Measured chloride "+fmt(cl.v)+" mEq/L is extreme. Confirm the sample quality, the value and the unit before acting on it.");
  if(S.flags.saline&&!S.flags.kbr){
    if(S.stC==="high") w("amber",3,"Caution. Chloride-rich fluids (0.9% NaCl, hypertonic saline, KCl supplementation) are the commonest iatrogenic cause of corrected hyperchloraemia. If the acidosis matters clinically, reassess the fluid choice; a balanced isotonic crystalloid may fit better.");
    else w("info",3,"Note. Chloride-rich fluids tend to raise the corrected chloride over time; recheck electrolytes after any fluid change.");
  }
  if(S.flags.diuretic&&!S.flags.kbr){
    if(S.stC==="low") w("amber",4,"Caution. Loop and thiazide diuretics cause renal chloride loss and chloride-responsive alkalosis, consistent with this result. Check potassium as well; it is often low in the same patients.");
    else w("info",4,"Note. Loop and thiazide diuretics promote chloride loss; monitor electrolytes on ongoing therapy.");
  }
  if(S.flags.vomit&&!S.flags.kbr){
    if(S.stC==="low") w("amber",5,"Caution. Gastric chloride (HCl) loss from vomiting or pyloric outflow obstruction is the classic cause of corrected hypochloraemia, consistent with this result. Consider imaging for an obstruction if not already excluded.");
    else w("info",5,"Note. Vomiting of stomach contents selectively loses chloride; recheck electrolytes if vomiting continues.");
  }
  if((S.stM!=="ok"||S.stC!=="ok")&&!S.flags.kbr) w("info",9,"Note. The correction assumes chloride normally tracks sodium; it is an estimate, not a measurement. Confirm acid-base status with a blood gas where possible.");
  var rank={red:0,amber:1,info:2};
  W.sort(function(a,b){var d=rank[a.lvl]-rank[b.lvl];return d!==0?d:a.kind-b.kind;});

  return S;
}

/* report text shared by copy / email / download / print (built once per state) */
function buildReport(S,opts){
  opts=opts||{};
  var L=[];
  var stTxt={ok:"within the reference interval",low:"LOW",high:"HIGH"};
  L.push("ASK A VET(TM) Corrected chloride");
  if(opts.date) L.push("Generated: "+opts.date);
  L.push("");
  L.push("Species: "+REF[S.sp].label);
  L.push("Measured Na+: "+fmt(S.na.entered)+" "+UNITS[S.na.unit]+(S.na.unit==="mgdl"?" ("+fmt(S.na.v)+" mmol/L)":""));
  L.push("Measured Cl-: "+fmt(S.cl.entered)+" "+UNITS[S.cl.unit]+(S.cl.unit==="mgdl"?" ("+fmt(S.cl.v)+" mmol/L)":""));
  L.push("Normal Na+ (midpoint): "+fmt(S.nn)+" mEq/L");
  L.push("Chloride reference interval: "+fmt(S.riLo)+" to "+fmt(S.riHi)+" mEq/L");
  L.push("");
  L.push("RESULT");
  L.push("Corrected Cl-: "+fmt1(S.corr)+" mEq/L ("+(S.flags.kbr?"not interpretable on bromide":stTxt[S.stC])+")");
  L.push("Measured Cl-: "+fmt(S.cl.v)+" mEq/L ("+stTxt[S.stM]+")");
  L.push("Free water effect on chloride: "+(S.water>0?"+":"")+fmt1(S.water)+" mEq/L");
  L.push("");
  L.push("INTERPRETATION");
  L.push(opts.shortInterp?(S.interp.title+"."):(S.interp.title+". "+S.interp.text));
  var checks=opts.dropInfoChecks?S.warnings.filter(function(x){return x.lvl!=="info";}):S.warnings;
  if(checks.length){
    L.push("");
    L.push("CHECKS");
    for(var i=0;i<checks.length;i++) L.push("- "+checks[i].text);
  }
  if(!opts.skipWorking){
    L.push("");
    L.push("WORKING");
    L.push("Corrected Cl- = (normal Na+ / measured Na+) x measured Cl-");
    for(var j=0;j<S.workingLines.length;j++) L.push(S.workingLines[j]);
  }
  if(!opts.skipFooter){
    L.push("");
    L.push("Professional use only. Confirm acid-base status with a blood gas where possible and use your laboratory's reference intervals.");
  }
  if(opts.url) L.push(opts.url);
  return L.join("\n");
}

var ENGINE={REF:REF,CONV:CONV,LIMITS:LIMITS,parseNum:parseNum,fmt:fmt,fmt1:fmt1,statusOf:statusOf,toMmol:toMmol,computeCore:computeCore,buildReport:buildReport};
if(typeof window!=="undefined") window.AAVCC=ENGINE;
if(typeof module!=="undefined"&&module.exports) module.exports=ENGINE;
if(typeof document==="undefined") return;

/* ============================ UI ============================ */
function el(id){return ROOT.querySelector("#"+id);}
function esc(s){return String(s).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");}

var ui={sp:"dog",naUnit:"meq",clUnit:"meq",dirty:false};
var lastState=null;

function segVal(groupAttr){
  var b=ROOT.querySelector("["+groupAttr+"][aria-pressed='true']");
  return b?b.getAttribute(groupAttr):null;
}
function setSeg(groupAttr,val){
  var bs=ROOT.querySelectorAll("["+groupAttr+"]");
  for(var i=0;i<bs.length;i++) bs[i].setAttribute("aria-pressed",bs[i].getAttribute(groupAttr)===val?"true":"false");
}

function gatherInputs(){
  return {
    sp:ui.sp,
    naRaw:el("naVal").value, naUnit:segVal("data-nau"),
    clRaw:el("clVal").value, clUnit:segVal("data-clu"),
    nnRaw:el("nnVal").value,
    riLoRaw:el("riLo").value, riHiRaw:el("riHi").value,
    flags:{kbr:el("fKbr").checked,saline:el("fSaline").checked,diuretic:el("fDiuretic").checked,vomit:el("fVomit").checked}
  };
}

/* echoes + inline errors */
function setEcho(id,msg,isErr){
  var e=el(id);
  e.textContent=msg||"";
  e.classList.toggle("err",!!isErr);
}
function echoFor(raw,unit,ion){
  var p=parseNum(raw);
  if(p===null||isNaN(p)) return "";
  if(unit==="mgdl") return fmt(p)+" mg/dL = "+fmt1(toMmol(p,unit,ion))+" mmol/L ("+fmt1(toMmol(p,unit,ion))+" mEq/L)";
  return "= "+fmt(p)+" "+(unit==="meq"?"mmol/L":"mEq/L")+" (identical)";
}

function render(){
  var inp=gatherInputs();
  var S=computeCore(inp);
  lastState=S;

  setEcho("naEcho",S.errors.na?S.errors.na:echoFor(inp.naRaw,inp.naUnit,"na"),!!S.errors.na);
  setEcho("clEcho",S.errors.cl?S.errors.cl:echoFor(inp.clRaw,inp.clUnit,"cl"),!!S.errors.cl);
  setEcho("nnEcho",S.errors.nn?S.errors.nn:"",!!S.errors.nn);
  setEcho("riEcho",S.errors.ri?S.errors.ri:"",!!S.errors.ri);

  var h="";
  if(S.blocked){
    h='<div class="bigcard"><div class="bignum" aria-hidden="true">&ndash;</div><div class="runit">mEq/L (mmol/L)</div><span class="chip blocked">Waiting for values</span></div>';
    h+='<div class="blockedmsg">'+esc(S.blockedMsg)+'</div>';
  } else {
    var chipCls=S.flags.kbr?"redc":(S.stC==="ok"?"ok":S.stC);
    var chipTxt=S.flags.kbr?"On bromide: not interpretable":(S.stC==="ok"?"Within interval":(S.stC==="low"?"Below interval":"Above interval"));
    var stWord={ok:"within",low:"low",high:"high"};
    h+='<div class="bigcard"><div class="bignum'+(S.flags.kbr?' kbr':'')+'">'+fmt1(S.corr)+(S.flags.kbr?'<span class="sr-only"> (not interpretable on bromide)</span>':'')+'</div><div class="runit">mEq/L (mmol/L), against '+fmt(S.riLo)+' to '+fmt(S.riHi)+'</div><span class="chip '+chipCls+'">'+esc(chipTxt)+'</span></div>';
    for(var i=0;i<S.warnings.length;i++) h+='<div class="warn '+S.warnings[i].lvl+'">'+esc(S.warnings[i].text)+'</div>';
    h+='<div class="interp"><b>'+esc(S.interp.title)+'.</b> '+esc(S.interp.text)+'</div>';
    h+='<div class="cmp">'
      +'<div class="cbox"><div class="clab">Measured Cl<sup>&minus;</sup></div><div class="cval">'+fmt(S.cl.v)+'</div><div class="cst '+(S.stM==="ok"?"ok":S.stM)+'">'+stWord[S.stM]+'</div></div>'
      +'<div class="cbox"><div class="clab">Corrected Cl<sup>&minus;</sup></div><div class="cval">'+fmt1(S.corr)+'</div><div class="cst '+(S.stC==="ok"?"ok":S.stC)+'">'+stWord[S.stC]+'</div></div>'
      +'</div>';
    h+='<div class="metaline">Free water effect on chloride: '+(S.water>0?"+":"")+fmt1(S.water)+' mEq/L '+(S.water>0?'(water excess was masking this much chloride)':(S.water<0?'(water deficit was inflating the measured value)':'(sodium is at the entered normal midpoint)'))+'</div>';
    h+='<div><p class="wtitle">Working</p><div class="working" tabindex="0" role="region" aria-label="Working">Corrected Cl&#8722; = (normal Na&#8314; &divide; measured Na&#8314;) &times; measured Cl&#8722;\n'+esc(S.workingLines.join("\n")).replace(/ \/ /g," &divide; ").replace(/ x /g," &times; ")+'</div></div>';
  }
  el("rbody").innerHTML=h;

  function setInvalid(id,bad){var e=el(id);if(bad)e.setAttribute("aria-invalid","true");else e.removeAttribute("aria-invalid");}
  setInvalid("naVal",!!S.errors.na);setInvalid("clVal",!!S.errors.cl);setInvalid("nnVal",!!S.errors.nn);
  setInvalid("riLo",!!S.errors.ri);setInvalid("riHi",!!S.errors.ri);

  renderPinbar(S);
  scheduleSr(S);
  if(ui.dirty) scheduleHash();
}

/* pinbar */
function renderPinbar(S){
  var bar=el("pinbar"),val=el("pbval"),chip=el("pbchip");
  bar.classList.remove("pb-ok","pb-amber","pb-red");
  if(S.blocked){
    val.textContent="–";
    chip.textContent=S.blockedKind==="errors"?"Fix highlighted values":(S.blockedKind==="ref"?"Enter reference values":"Enter Na and Cl");
    chip.className="pbchip";
  } else {
    val.textContent=fmt1(S.corr);
    var hasRed=S.warnings.some(function(x){return x.lvl==="red";});
    var hasAmber=S.warnings.some(function(x){return x.lvl==="amber";});
    if(hasRed){
      chip.textContent="See warnings";
      chip.className="pbchip red";
      bar.classList.add("pb-red");
    } else if(S.stC==="ok"&&hasAmber){
      chip.textContent="Cautions apply";
      chip.className="pbchip low";
      bar.classList.add("pb-amber");
    } else if(S.stC==="ok"){
      chip.textContent="Within interval";
      chip.className="pbchip ok";
      bar.classList.add("pb-ok");
    } else {
      chip.textContent=S.stC==="low"?"Below interval":"Above interval";
      chip.className="pbchip "+S.stC;
      bar.classList.add("pb-amber");
    }
  }
  padForPinbar();
}
function padForPinbar(){
  var shell=ROOT.querySelector(".aav-shell");
  var bar=el("pinbar");
  var visible=window.matchMedia("(max-width:899px)").matches;
  shell.style.paddingBottom=visible?(bar.offsetHeight+24)+"px":"";
}
window.addEventListener("resize",padForPinbar);

/* debounced sr summary */
var srT=null;
function scheduleSr(S){
  clearTimeout(srT);
  srT=setTimeout(function(){
    var msg;
    if(S.blocked){
      if(S.blockedKind==="errors"){
        var errs=[S.errors.na,S.errors.cl,S.errors.nn,S.errors.ri].filter(Boolean).join(" ");
        msg="Cannot calculate. "+errs;
      } else if(S.blockedKind==="ref") msg="Waiting for the reference values in step 3.";
      else msg="Waiting for sodium and chloride values.";
    }
    else{
      var reds=S.warnings.filter(function(x){return x.lvl==="red";}).length;
      var ambers=S.warnings.filter(function(x){return x.lvl==="amber";}).length;
      msg=(S.flags.kbr
        ?"Corrected chloride not interpretable: patient on bromide."
        :"Corrected chloride "+fmt1(S.corr)+" milliequivalents per litre, "+(S.stC==="ok"?"within the reference interval":(S.stC==="low"?"below the reference interval":"above the reference interval"))+".")
        +(reds?" "+reds+" critical warning"+(reds>1?"s":"")+".":"")
        +(ambers?" "+ambers+" caution"+(ambers>1?"s":"")+".":"");
    }
    el("srlive").textContent=msg;
  },900);
}

/* hash state (written only after user interaction; anchors left alone otherwise) */
var hashT=null;
function buildHash(){
  var f=[];
  if(el("fKbr").checked)f.push("k");
  if(el("fSaline").checked)f.push("s");
  if(el("fDiuretic").checked)f.push("d");
  if(el("fVomit").checked)f.push("v");
  return "#sp="+ui.sp
    +"&na="+encodeURIComponent(el("naVal").value.trim())
    +"&nau="+segVal("data-nau")
    +"&cl="+encodeURIComponent(el("clVal").value.trim())
    +"&clu="+segVal("data-clu")
    +"&nn="+encodeURIComponent(el("nnVal").value.trim())
    +"&rl="+encodeURIComponent(el("riLo").value.trim())
    +"&rh="+encodeURIComponent(el("riHi").value.trim())
    +"&f="+f.join(",");
}
function scheduleHash(){
  clearTimeout(hashT);
  hashT=setTimeout(function(){
    try{history.replaceState(null,"",location.pathname+location.search+buildHash());}catch(e){}
  },400);
}
function restoreHash(){
  var hash=location.hash;
  if(!hash||hash.indexOf("=")<0){ /* plain anchor: open its details */
    if(hash){var t=document.getElementById(hash.slice(1));if(t&&t.tagName==="DETAILS")t.open=true;}
    return false;
  }
  var q={};
  hash.slice(1).split("&").forEach(function(p){var kv=p.split("=");try{q[kv[0]]=decodeURIComponent(kv[1]||"");}catch(e){}});
  var sp=(q.sp==="dog"||q.sp==="cat"||q.sp==="other")?q.sp:"dog";
  applySpecies(sp,false);
  var nau=(q.nau==="meq"||q.nau==="mmol"||q.nau==="mgdl")?q.nau:"meq";
  var clu=(q.clu==="meq"||q.clu==="mmol"||q.clu==="mgdl")?q.clu:"meq";
  setSeg("data-nau",nau); setSeg("data-clu",clu);
  /* restore faithfully, even when unparseable: assignment to .value is inert, and the
     render path then shows the sender's exact inline error instead of silently
     substituting a default that computes a number the sender never saw */
  function safeSet(id,v){ if(v==null)return; el(id).value=String(v).slice(0,40); }
  safeSet("naVal",q.na); safeSet("clVal",q.cl);
  safeSet("nnVal",q.nn); safeSet("riLo",q.rl); safeSet("riHi",q.rh);
  var f=(q.f||"").split(",");
  el("fKbr").checked=f.indexOf("k")>=0;
  el("fSaline").checked=f.indexOf("s")>=0;
  el("fDiuretic").checked=f.indexOf("d")>=0;
  el("fVomit").checked=f.indexOf("v")>=0;
  return true;
}

/* species */
function applySpecies(sp,announce){
  ui.sp=sp;
  var bs=ROOT.querySelectorAll("[data-sp]");
  for(var i=0;i<bs.length;i++) bs[i].setAttribute("aria-pressed",bs[i].getAttribute("data-sp")===sp?"true":"false");
  var r=REF[sp];
  el("nnVal").value=r.nn==null?"":String(r.nn);
  el("riLo").value=r.lo==null?"":String(r.lo);
  el("riHi").value=r.hi==null?"":String(r.hi);
  var note=el("seedNote");
  if(announce){
    note.textContent=(sp==="other")
      ?"Enter your own normal sodium and chloride interval below; there are no defaults for other species."
      :"Reference values set to "+r.label+" defaults: normal Na "+r.nn+", interval "+r.lo+" to "+r.hi+" mEq/L. Replace them with your laboratory's values if they differ.";
    note.classList.add("show");
  }
}

/* actions */
function nowStr(){
  var d=new Date();
  function p(n){return (n<10?"0":"")+n;}
  return d.getFullYear()+"-"+p(d.getMonth()+1)+"-"+p(d.getDate())+" "+p(d.getHours())+":"+p(d.getMinutes());
}
function flash(btn,txt){
  var old=btn.textContent;
  btn.textContent=txt;
  setTimeout(function(){btn.textContent=old;},1500);
}
function reportOrNull(){
  if(!lastState||lastState.blocked){return null;}
  return lastState;
}
function copyText(txt,btn,okMsg){
  function fallback(){
    var t=document.createElement("textarea");
    t.value=txt;t.setAttribute("readonly","");t.style.position="absolute";t.style.left="-9999px";
    document.body.appendChild(t);t.select();
    try{document.execCommand("copy");flash(btn,okMsg);}catch(e){flash(btn,"Copy failed");}
    t.remove();
  }
  if(navigator.clipboard&&navigator.clipboard.writeText){
    navigator.clipboard.writeText(txt).then(function(){flash(btn,okMsg);},fallback);
  } else fallback();
}
function shareUrl(){return location.origin+location.pathname+location.search+buildHash();}

el("btnCopy").addEventListener("click",function(){
  var S=reportOrNull();
  if(!S){flash(el("btnCopy"),"Enter values first");return;}
  copyText(buildReport(S,{date:nowStr(),url:shareUrl()}),el("btnCopy"),"Copied");
});
el("btnEmail").addEventListener("click",function(){
  var S=reportOrNull();
  if(!S){flash(el("btnEmail"),"Enter values first");return;}
  var subject="Corrected chloride "+fmt1(S.corr)+" mEq/L ("+REF[S.sp].label+") - ASK A VET";
  /* progressive degradation, re-checked at each step; red and amber CHECKS are never dropped */
  var variants=[
    {date:nowStr(),url:shareUrl()},
    {date:nowStr(),skipWorking:true,url:shareUrl()},
    {date:nowStr(),skipWorking:true},
    {date:nowStr(),skipWorking:true,skipFooter:true},
    {date:nowStr(),skipWorking:true,skipFooter:true,shortInterp:true},
    {date:nowStr(),skipWorking:true,skipFooter:true,shortInterp:true,dropInfoChecks:true}
  ];
  var body=null;
  for(var i=0;i<variants.length;i++){
    var b=buildReport(S,variants[i]);
    if(("mailto:?subject="+encodeURIComponent(subject)+"&body="+encodeURIComponent(b)).length<=1900){body=b;break;}
  }
  if(body===null) body=buildReport(S,variants[variants.length-1]);
  location.href="mailto:?subject="+encodeURIComponent(subject)+"&body="+encodeURIComponent(body);
});
el("btnDownload").addEventListener("click",function(){
  var S=reportOrNull();
  if(!S){flash(el("btnDownload"),"Enter values first");return;}
  var txt=buildReport(S,{date:nowStr(),url:shareUrl()});
  var d=new Date();
  function p(n){return (n<10?"0":"")+n;}
  var fn="corrected-chloride-"+d.getFullYear()+p(d.getMonth()+1)+p(d.getDate())+".txt";
  (async function(){
    try{
      if(window.claude&&typeof window.claude.use==="function"){
        var dl=await window.claude.use("downloads");
        if(dl){await dl.save({filename:fn,data:txt});flash(el("btnDownload"),"Saved");return;}
      }
    }catch(e){}
    var b=new Blob([txt],{type:"text/plain"});
    var u=URL.createObjectURL(b);
    var a=document.createElement("a");
    a.href=u;a.download=fn;document.body.appendChild(a);a.click();
    setTimeout(function(){URL.revokeObjectURL(u);a.remove();},500);
    flash(el("btnDownload"),"Downloaded");
  })();
});
el("btnShare").addEventListener("click",function(){
  var url=shareUrl();
  if(navigator.share){
    navigator.share({title:"Corrected chloride calculator - ASK A VET",url:url}).catch(function(){});
  } else copyText(url,el("btnShare"),"Link copied");
});

/* print */
function buildPrint(){
  var P=el("aav-print");
  var S=lastState;
  var d=nowStr();
  var h='<div class="phead"><div><div class="ph1">Corrected chloride record</div><div class="pmeta">ASK A VET(TM) vet tools &middot; askavet.com &middot; Printed '+esc(d)+'</div></div><div class="pbrand">ASK A VET&trade;</div></div>';
  h+='<div class="pline">Patient: ______________________________ &nbsp;&nbsp; Case no: ______________ &nbsp;&nbsp; Clinician: ______________________________</div>';
  if(!S||S.blocked){
    h+='<p>No calculation was completed on screen. Formula: corrected Cl- = (normal Na+ / measured Na+) x measured Cl-.</p>';
  } else {
    var stTxt={ok:"within interval",low:"LOW",high:"HIGH"};
    h+='<table><thead><tr><th>Input</th><th>Value</th></tr></thead><tbody>'
      +'<tr><td>Species</td><td>'+esc(REF[S.sp].label)+'</td></tr>'
      +'<tr><td>Measured Na+</td><td>'+fmt(S.na.entered)+' '+UNITS[S.na.unit]+(S.na.unit==="mgdl"?' ('+fmt(S.na.v)+' mmol/L)':'')+'</td></tr>'
      +'<tr><td>Measured Cl-</td><td>'+fmt(S.cl.entered)+' '+UNITS[S.cl.unit]+(S.cl.unit==="mgdl"?' ('+fmt(S.cl.v)+' mmol/L)':'')+'</td></tr>'
      +'<tr><td>Normal Na+ (midpoint)</td><td>'+fmt(S.nn)+' mEq/L</td></tr>'
      +'<tr><td>Chloride reference interval</td><td>'+fmt(S.riLo)+' to '+fmt(S.riHi)+' mEq/L</td></tr>'
      +'</tbody></table>';
    h+='<table><thead><tr><th>Result</th><th>Value</th><th>Classification</th></tr></thead><tbody>'
      +'<tr><td>Corrected Cl-</td><td>'+fmt1(S.corr)+' mEq/L</td><td>'+(S.flags.kbr?'not interpretable (bromide)':stTxt[S.stC])+'</td></tr>'
      +'<tr><td>Measured Cl-</td><td>'+fmt(S.cl.v)+' mEq/L</td><td>'+stTxt[S.stM]+'</td></tr>'
      +'<tr><td>Free water effect</td><td>'+(S.water>0?"+":"")+fmt1(S.water)+' mEq/L</td><td></td></tr>'
      +'</tbody></table>';
    h+='<div class="pworking">Corrected Cl- = (normal Na+ / measured Na+) x measured Cl-\n'+esc(S.workingLines.join("\n"))+'</div>';
    h+='<p><b>'+esc(S.interp.title)+'.</b> '+esc(S.interp.text)+'</p>';
    for(var i=0;i<S.warnings.length;i++) h+='<div class="pwarn '+S.warnings[i].lvl+'">'+esc(S.warnings[i].text)+'</div>';
  }
  h+='<div class="psig"><div>Clinician signature</div><div>Checked by</div></div>';
  h+='<div class="pfoot">Professional use only. Interpretive aid, not a diagnosis; confirm acid-base status with a blood gas where possible and use your laboratory\'s reference intervals. askavet.com/pages/corrected-chloride-calculator</div>';
  P.innerHTML=h;
}
el("btnPrint").addEventListener("click",function(){buildPrint();window.print();});
window.addEventListener("beforeprint",buildPrint);
if(window.matchMedia){
  var mq=window.matchMedia("print");
  if(mq&&mq.addListener) mq.addListener(function(m){if(m.matches)buildPrint();});
}

/* jumps */
function jumpToResult(){
  var r=el("aav-result");
  r.setAttribute("tabindex","-1");
  var reduce=window.matchMedia&&window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  r.scrollIntoView({behavior:reduce?"auto":"smooth",block:"start"});
  r.focus({preventScroll:true});
}
el("pbjump").addEventListener("click",jumpToResult);
el("jumpbtn").addEventListener("click",jumpToResult);

/* TOC anchors + deep links open their details */
ROOT.querySelectorAll('.toc a[href^="#"]').forEach(function(a){
  a.addEventListener("click",function(){
    var t=document.getElementById(a.getAttribute("href").slice(1));
    if(t&&t.tagName==="DETAILS") t.open=true;
  });
});
window.addEventListener("hashchange",function(){
  var hsh=location.hash;
  if(hsh&&hsh.indexOf("=")<0){
    var t=document.getElementById(hsh.slice(1));
    if(t&&t.tagName==="DETAILS") t.open=true;
  } else if(hsh){
    /* a state link pasted into the open page: apply it, so screen and URL agree */
    try{restoreHash();ui.dirty=true;render();}catch(e){}
  }
});

/* inputs */
["naVal","clVal","nnVal","riLo","riHi"].forEach(function(id){
  el(id).addEventListener("input",function(){ui.dirty=true;render();});
});
["fKbr","fSaline","fDiuretic","fVomit"].forEach(function(id){
  el(id).addEventListener("change",function(){ui.dirty=true;render();});
});
ROOT.querySelectorAll("[data-sp]").forEach(function(b){
  b.addEventListener("click",function(){ui.dirty=true;applySpecies(b.getAttribute("data-sp"),true);render();});
});
ROOT.querySelectorAll("[data-nau]").forEach(function(b){
  b.addEventListener("click",function(){ui.dirty=true;setSeg("data-nau",b.getAttribute("data-nau"));render();});
});
ROOT.querySelectorAll("[data-clu]").forEach(function(b){
  b.addEventListener("click",function(){ui.dirty=true;setSeg("data-clu",b.getAttribute("data-clu"));render();});
});
el("nnUse").addEventListener("click",function(){
  var lo=parseNum(el("naLo").value),hi=parseNum(el("naHi").value);
  var echoEl=el("nnHelpEcho");
  if(lo==null||hi==null||isNaN(lo)||isNaN(hi)){echoEl.textContent="Enter both ends of the interval as plain numbers.";echoEl.classList.add("err");return;}
  if(lo>=hi){echoEl.textContent="The low end must be below the high end.";echoEl.classList.add("err");return;}
  var mid=Number(fmt((lo+hi)/2));
  el("nnVal").value=String(mid);
  echoEl.classList.remove("err");
  echoEl.textContent="Midpoint = ("+fmt(lo)+" + "+fmt(hi)+") / 2 = "+fmt(mid)+" mEq/L, applied above.";
  ui.dirty=true;
  render();
});

/* integrity check: static tables vs live constants */
function integrityCheck(){
  var bad=false;
  try{
    var rows=ROOT.querySelectorAll("#refValsTable tbody tr[data-sp]");
    if(rows.length!==2) bad=true;
    rows.forEach(function(r){
      var sp=r.getAttribute("data-sp"),ref=REF[sp];
      if(!ref){bad=true;return;}
      var nnCell=r.querySelector('[data-k="nn"]'),riCell=r.querySelector('[data-k="ri"]');
      if(!nnCell||nnCell.textContent.trim()!==String(ref.nn)) bad=true;
      if(!riCell||riCell.textContent.trim()!==(ref.lo+" to "+ref.hi)) bad=true;
    });
    var clCell=ROOT.querySelector('#unitTable [data-k="clconv"]');
    var naCell=ROOT.querySelector('#unitTable [data-k="naconv"]');
    var meqCell=ROOT.querySelector('#unitTable [data-k="meq"]');
    if(!clCell||clCell.textContent.indexOf(String(CONV.cl))<0) bad=true;
    if(!naCell||naCell.textContent.indexOf(String(CONV.na))<0) bad=true;
    if(!meqCell||meqCell.textContent.indexOf("× 1")<0) bad=true;
    /* FAQ schema vs visible FAQs */
    var ld=null;
    ROOT.querySelectorAll('script[type="application/ld+json"]').forEach(function(s){
      try{
        var j=JSON.parse(s.textContent);
        (j["@graph"]||[]).forEach(function(n){if(n["@type"]==="FAQPage")ld=n;});
      }catch(e){bad=true;}
    });
    var h3s=ROOT.querySelectorAll("#faqBody h3");
    if(!ld||!ld.mainEntity||ld.mainEntity.length!==h3s.length) bad=true;
    else for(var i=0;i<h3s.length;i++){
      if(ld.mainEntity[i].name.trim()!==h3s[i].textContent.trim()) bad=true;
    }
  }catch(e){bad=true;}
  if(bad) el("driftBanner").classList.add("show");
}

/* init */
applySpecies(ROOT.dataset.defaultSpecies === "cat" ? "cat" : (ROOT.dataset.defaultSpecies === "other" ? "other" : "dog"),false);
var restored=false;
try{restored=restoreHash();}catch(e){}
if(restored) ui.dirty=true;
render();
integrityCheck();
  }
}

if (!customElements.get("s-corrected-chloride-calculator")) {
  customElements.define("s-corrected-chloride-calculator", CorrectedChlorideCalculator);
}
